import React, { useState } from 'react';
import { Award, GraduationCap, Heart, Users, X, ArrowRight } from 'lucide-react';

const MyStoryModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/60 z-50 overflow-y-auto" onClick={onClose}>
      <div className="min-h-full flex items-center justify-center p-4 py-8">
        <div className="bg-white rounded-3xl max-w-3xl w-full shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="bg-[#1a365d] p-6 relative">
            <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30">
              <X className="w-4 h-4 text-white" />
            </button>
            <h3 className="text-2xl font-bold text-white">Moja zgodba</h3>
            <p className="text-white/70 text-sm mt-1">Saša Panič, dipl. fiziot., Cyriax terapevtka</p>
          </div>
          
          <div className="p-6">
            {/* Image and first paragraph */}
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <img loading="lazy" decoding="async" 
                src="/images/fizioterapevtka-sasa-panic-portret.jpg"
                alt="Saša Panič - Fizioterapevtka"
                className="w-32 h-32 sm:w-40 sm:h-40 object-cover rounded-2xl flex-shrink-0"
              />
              <p className="text-slate-700 leading-relaxed">
                Kot vrhunska športnica in košarkarica sem se velikokrat srečevala s poškodbami. Tako lastnimi, kot tudi s poškodbami drugih. Že takrat sem vedela, da želim postati fizioterapevtka in ljudem kar se da kvalitetno in strokovno pomagati pri rehabilitaciji njihovih poškodb in bolečin. Ves čas študija sem bila vpeta v aktivno igranje košarke, poškodbe ter njihovo rehabilitacijo.
              </p>
            </div>
            
            <div className="space-y-4 text-slate-700 leading-relaxed">
              <p>
                Po končanem študiju na Zdravstveni fakulteti v Ljubljani sem izkušnje pridobivala v različnih športih klubih, kjer sem igrala, prav tako pa sem se kot fizioterapevtka udeleževala reprezentančnih akcij, evropskih prvenstev, košarkarskih taborov. V tem času sem opravila dodatne tečaje manualne terapije (Cyriax, Mulligan, IASTM, mobilizacija živčevja), prav tako pa sem željna novih znanj in izkušenj, zato se vseskozi dodatno izobražujem.
              </p>
              
              <p>
                Moja samostojna pot v fizioterapiji se je pravzaprav pričela že pred leti, med drugim sem opravljala delo vodje fizioterapije v ZD Ljubljana-Vič ter po nekaj letnih izušnjah sem se odločila stopiti na lastno pot.
              </p>
              
              <p>
                Trenutno izvajam strokovno fizioterapevtsko obravnavo poškodb športnikov in tudi ostalih s poškodbami in bolečinami. Sem mama 12-letnemu sinu in 9-letni hčerki. Izkušnja dveh nosečnosti in porodov, nosečniških tegob, poporodnih situacij in stanj telesa mi je dala poleg izobraževanja na področju zdravja žensk še dodatno izkušnjo in razumevanje bodočih mamic in žensk po porodu.
              </p>
            </div>
          </div>
          
          <div className="p-6 pt-0">
            <button 
              onClick={onClose}
              className="w-full bg-[#f87171] hover:bg-[#e05858] text-white py-3 rounded-full font-semibold transition-colors"
            >
              Zapri
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AboutSection = () => {
  const [showStory, setShowStory] = useState(false);
  
  const achievements = [
    {
      icon: GraduationCap,
      title: 'Izobrazba',
      description: 'S številnimi specializacijami',
    },
    {
      icon: Award,
      title: 'Izkušnje',
      description: 'Več kot 15 let dela s pacienti vseh starosti',
    },
    {
      icon: Heart,
      title: 'Pristop',
      description: 'Individualna obravnava in celostni pristop k zdravljenju',
    },
    {
      icon: Users,
      title: 'Rezultati',
      description: 'Več kot 1000 zadovoljnih pacientov',
    },
  ];

  return (
    <>
    <section
      id="o-meni"
      className="py-10 md:py-16 bg-white"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Banner Layout - Image left, Text right */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Image Side */}
          <div className="flex justify-center lg:justify-start">
            <img
              src="/images/fizioterapija-oprema-ultrazvok.png"
              alt="Saša Panič - Fizioterapija in certifikati"
              className="w-full max-w-md h-auto object-contain"
              loading="lazy"
            />
          </div>

          {/* Text Side */}
          <div>
            <span className="inline-block text-[#f87171] font-semibold tracking-wider uppercase text-sm mb-4">
              O meni
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a365d] mb-4">
              Znanje, ki stoji za vsakim gibom
            </h2>
            <p className="text-slate-600 text-lg mb-6">
              Saša Panič, dipl. fiziot., Cyriax terapevtka
            </p>
            <p className="text-slate-600 text-base leading-relaxed mb-6">
              Sem Saša Panič, fizioterapevtka z več kot 15-letnimi izkušnjami 
              na področju fizioterapije in rehabilitacije. Moja misija je najti pravo pot 
              do vašega optimalnega gibanja in kvalitetnega življenja brez bolečin.
            </p>
            <p className="text-slate-600 text-base leading-relaxed mb-6">
              Specializirana sem za manualno terapijo, športno rehabilitacijo in obravnavo 
              kroničnih bolečinskih stanj. Vsak pacient je edinstven, zato se k vsakemu 
              približam individualno in celovito.
            </p>
            
            {/* My Story Button */}
            <button
              onClick={() => setShowStory(true)}
              className="inline-flex items-center gap-2 text-[#f87171] hover:text-[#e05858] font-semibold mb-8 transition-colors"
              data-testid="my-story-btn"
            >
              Moja zgodba
              <ArrowRight className="w-4 h-4" />
            </button>

            {/* Achievements Grid */}
            <div className="grid grid-cols-2 gap-4">
              {achievements.map((achievement, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#f87171]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <achievement.icon className="w-5 h-5 text-[#f87171]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1a365d] text-sm mb-0.5">{achievement.title}</h4>
                    <p className="text-slate-500 text-xs">{achievement.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
    
    {/* My Story Modal */}
    <MyStoryModal isOpen={showStory} onClose={() => setShowStory(false)} />
    </>
  );
};

export default AboutSection;
